import { useEffect } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import {
  GetAllAlertsQuery,
  GetAllAlertsQueryVariables,
} from "../../../../../API";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { GET_ALL_ALERTS } from "../../../../../common/operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../../../common/variables/common";
import { errorNotification } from "../../../../../common/variables/notification";

export const ALERTS_TABLE_LIMIT = 10;

export const useGetAlertsCached = () => {
  const selectedCustomerId = useCustomerIdGuard();

  const [getAlerts, { data, fetchMore, loading, networkStatus }] = useLazyQuery<
    GetAllAlertsQuery,
    GetAllAlertsQueryVariables
  >(GET_ALL_ALERTS, {
    variables: {
      customerId: selectedCustomerId,
      limit: DEFAULT_LIMIT_COUNT,
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!selectedCustomerId) return;

    getAlerts({
      variables: {
        customerId: selectedCustomerId,
        limit: DEFAULT_LIMIT_COUNT,
      },
    });
  }, [selectedCustomerId, getAlerts]);

  useEffect(() => {
    if (!data?.getAllAlerts?.nextToken) return;

    const fetchAllPages = async () => {
      let currentNextToken = data.getAllAlerts.nextToken;

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              customerId: selectedCustomerId,
              limit: DEFAULT_LIMIT_COUNT,
              nextToken: currentNextToken,
            },
          });

          currentNextToken = response.data?.getAllAlerts?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional alert pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [data?.getAllAlerts?.nextToken, selectedCustomerId, fetchMore]);

  const alertsLoading = loading || networkStatus === NetworkStatus.fetchMore;

  return {
    data,
    loading: alertsLoading,
  };
};
